// MobileDrawerV2 — slide-in drawer for the responsive v2 chrome.
//
// Replaces both AppRailV2 (left nav strip) and the per-view inline list
// sidebars (DashboardsDrawerV2, ChatsHistoryV2) when the viewport is
// below `lg`. Layout, top to bottom:
//
//   1. Brand header — logo + product name.
//   2. Nav icons + labels — Dashboards, Data Explorer. The "Composer"
//      view from the desktop rail is intentionally excluded on small
//      screens (the user's spec; deep-links to /composer redirect to
//      /board, handled in App.tsx).
//   3. Divider.
//   4. **Context panel slot** — the active view's list, passed in via
//      the `contextPanel` prop. The host computes which list belongs
//      here based on the current route (sessions vs dashboards) and
//      hands the ready-rendered element down.
//   5. Footer — UserProfile.
//
// Earlier iterations used a `createPortal` target so views could
// portal their inline lists into this slot without the drawer knowing
// about either domain. The portal worked in isolation but stayed
// invisible in the wild (timing + cascade issues with the children's
// own desktop-column widths), so the drawer is now a plain consumer
// of a single ReactNode prop and the host decides what to render via
// route inspection.
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { DashboardIcon, DataExplorerIcon } from './icons';
import { themeTokens } from '@lib/themes/v2';

const DRAWER_WIDTH = 300;

export interface MobileDrawerV2NavItem {
    id: string;
    label: string;
    to: string;
    icon: React.ReactNode;
    isActive?: (pathname: string) => boolean;
}

// Default nav for the responsive chrome — mirrors the desktop AppRailV2
// list but drops the Composer entry (not available on mobile). Hosts
// can override this entirely via the `navItems` prop, e.g. to wire a
// different route map.
export const MOBILE_NAV_DEFAULT: MobileDrawerV2NavItem[] = [
    {
        id: 'dashboards',
        label: 'Dashboards',
        to: '/board',
        icon: <DashboardIcon size={18} />,
    },
    {
        id: 'data-explorer',
        label: 'Data Explorer',
        to: '/data-explorer',
        icon: <DataExplorerIcon size={18} />,
    },
];

export interface MobileDrawerV2Props {
    /** Controlled open state. */
    open: boolean;
    width?: string;
    /** Fired when the drawer requests to close (backdrop click, ESC, nav tap). */
    onClose: () => void;
    /** Override the nav-icon list. Defaults to Dashboards + Data Explorer. */
    navItems?: MobileDrawerV2NavItem[];
    /** Brand mark rendered at the top of the drawer. */
    logo?: React.ReactNode;
    /** Optional product name shown next to the logo. */
    productName?: string;
    /** Bottom-pinned content — typically the UserProfile menu. */
    userProfile?: React.ReactNode;
    /**
     * Sessions list element — typically a self-contained
     * `<MobileSessionsList />` adapter that fetches its own data.
     * Rendered between the nav and the user footer when the active
     * pathname starts with `/data-explorer`. The component is only
     * mounted when its route is active; passing both slots as JSX
     * does NOT mount both (React elements are descriptions until
     * committed).
     */
    sessionsList?: React.ReactNode;
    /**
     * Dashboards list element — `<MobileDashboardsList />` or
     * equivalent. Rendered when the active pathname starts with
     * `/board`.
     */
    dashboardsList?: React.ReactNode;
    /** When true, tapping a nav item also closes the drawer. Defaults true. */
    closeOnNavigate?: boolean;
}

export const MobileDrawerV2: React.FC<MobileDrawerV2Props> = ({
    open,
    onClose,
    navItems = MOBILE_NAV_DEFAULT,
    width = DRAWER_WIDTH,
    logo,
    productName = 'Agentic Explorer',
    userProfile,
    sessionsList,
    dashboardsList,
    closeOnNavigate = true,
}) => {
    const { pathname } = useLocation();

    // Route-driven contextPanel — picks the matching slot for the
    // active pathname. Both `sessionsList` and `dashboardsList` are
    // passed as JSX descriptions; only the one returned here is
    // actually committed (mounted), so the adapter for the inactive
    // route does no work.
    const contextPanel = pathname.startsWith('/data-explorer')
        ? sessionsList
        : pathname.startsWith('/board')
            ? dashboardsList
            : null;

    const navList = useMemo(
        () =>
            navItems.map((item) => {
                const active = item.isActive
                    ? item.isActive(pathname)
                    : pathname === item.to || pathname.startsWith(item.to + '/');
                return (
                    <Box
                        key={item.id}
                        component={Link}
                        to={item.to}
                        onClick={() => closeOnNavigate && onClose()}
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: `${theme.shape.radiusSm}px`,
                            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                            backgroundColor: active ? theme.palette.surface.hover : 'transparent',
                            textDecoration: 'none',
                            transition: 'background-color .12s, color .12s',
                            '&:hover': {
                                backgroundColor: theme.palette.surface.raised,
                                color: active ? theme.palette.primary.main : theme.palette.text.primary,
                            },
                        })}
                        aria-current={active ? 'page' : undefined}
                    >
                        {item.icon}
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'inherit' }}>
                            {item.label}
                        </Typography>
                    </Box>
                );
            }),
        [navItems, pathname, closeOnNavigate, onClose],
    );

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            // Keep the inner DOM mounted so the consumer's list keeps
            // its state (scroll position, expanded groups, in-progress
            // rename) across drawer open/close.
            ModalProps={{ keepMounted: true }}
            slotProps={{
                paper: {
                    sx: (theme) => ({
                        width: width,
                        maxWidth: '85vw',
                        backgroundColor: `${themeTokens.bgCanvasTransparency}`,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }),
                },
            }}
        >
            {(logo || productName) && (
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexShrink: 0,
                    })}
                >
                    {logo}
                    {productName && (
                        <Typography
                            sx={(theme) => ({
                                fontSize: 13,
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                            })}
                        >
                            {productName}
                        </Typography>
                    )}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    px: 1,
                    py: 1,
                    flexShrink: 0,
                }}
            >
                {navList}
            </Box>

            <Box
                sx={(theme) => ({
                    height: "1px",
                    backgroundColor: theme.palette.divider,
                    mx: 1.5,    
                    flexShrink: 0,
                })}
            />

            {/* Context panel — the route-specific list. The `& > *` rule
                re-bases the embedded list's desktop column width so it
                fills the drawer (both DashboardsDrawerV2 and
                ChatsHistoryV2 declare a `width: 280px` / `maxWidth:
                17vw` sized for their desktop position). The list also
                gets `flex: 1` so its internal scroll engages against
                the drawer's bounded height. */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '& > *': {
                        width: '100% !important',
                        maxWidth: 'none !important',
                        minWidth: 0,
                        flex: '1 1 auto !important',
                        borderRight: 'none !important',
                    },
                }}
            >
                {contextPanel}
            </Box>

            {userProfile && (
                <Box
                    sx={(theme) => ({
                        flexShrink: 0,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 1.5,
                        py: 1,
                    })}
                >
                    {userProfile}
                </Box>
            )}
        </Drawer>
    );
};

MobileDrawerV2.displayName = 'MobileDrawerV2';
